import NextImage, { ImageProps } from 'next/image'
import { getStoreConfig } from 'config'

const Image = ({ alt = '', ...props }: ImageProps): JSX.Element => (
    // Next 16 exige `alt`; muchos call sites CMS/legacy lo omiten o pasan
    // undefined. `''` marca la imagen como decorativa y evita el warning.
    <NextImage {...props} alt={alt ?? ''} loader={getFastlyUrl} />
)
const config = getStoreConfig()
const mediaBaseEnv = process.env.NEXT_PUBLIC_MEDIA_BASE_URL

// Absolute http(s)/data, or Magento protocol-relative (`//cdn/...`)
const patternForAbsoluteUrl = /^(data:|https?:|\/\/)/i

// Simple path joiner that guarantees one and only one slash between segments
const joinUrls = (base: string, url: string): string =>
    `${base.endsWith('/') ? base.slice(0, -1) : base}/${
        url.startsWith('/') ? url.slice(1) : url
    }`

// If the URL is relative, we need an absolute URL to make the URL object
const buildUrlFromRelative = (relative: string): string =>
    `${config?.base?.url?.mediaBackend?.baseMediaUrl}${relative}`

const toAbsoluteUrl = (url: string): string => {
    if (url.startsWith('//')) return `https:${url}`
    if (url.startsWith('http') || url.startsWith('data:')) return url
    return `https://${url.replace(/^\/+/, '')}`
}

type ImageLoaderProps = {
    src: string
    ignoreBaseUrl?: boolean
    width?: number
    quality?: number
}

export const getFastlyUrl = ({
    src,
    width,
    quality = 75,
    ignoreBaseUrl = false
}: ImageLoaderProps): string => {
    let parseSrc = src

    if (src.includes('{{') || src.includes('url=')) {
        const startIdx = src.indexOf('=')
        const endIdx = src.indexOf('}')
        parseSrc = src.slice(startIdx + 1, endIdx).trim()
        // Remove quotes if present
        if (
            (parseSrc.startsWith('"') && parseSrc.endsWith('"')) ||
            (parseSrc.startsWith("'") && parseSrc.endsWith("'"))
        ) {
            parseSrc = parseSrc.slice(1, -1)
        }
    }

    const isSourceAbsolute = patternForAbsoluteUrl.test(parseSrc)

    // if the URL is relative, add a sample base to it, just for purpose of creating the URL object
    let absoluteUrlStr = isSourceAbsolute
        ? toAbsoluteUrl(parseSrc)
        : buildUrlFromRelative(parseSrc)

    // Ensure we have a valid protocol
    if (
        !absoluteUrlStr.startsWith('http') &&
        !absoluteUrlStr.startsWith('data:')
    ) {
        absoluteUrlStr = toAbsoluteUrl(absoluteUrlStr)
    }

    let url
    try {
        url = new URL(absoluteUrlStr)
    } catch (e) {
        console.warn(
            '[getFastlyUrl] Invalid URL:',
            absoluteUrlStr,
            'from src:',
            src
        )
        return src
    }

    // if the media base env var is provided, use that as the media base url
    // if the env var is not provided, use the origin of the URL object
    const mediaBaseUrl =
        mediaBaseEnv && mediaBaseEnv !== '' ? mediaBaseEnv : url.origin

    const searchParams = new URLSearchParams(url.search)

    if (width) {
        searchParams.set('width', String(width))
    }

    searchParams.set('quality', String(quality))
    searchParams.set('auto', 'webp')
    searchParams.set('format', 'pjpg')

    if (ignoreBaseUrl) {
        return `${src}?${searchParams.toString()}`
    }

    return `${joinUrls(mediaBaseUrl, url.pathname)}?${searchParams.toString()}`
}

export default Image
